/**
 * Normalise a pathname so notes about the same SCREEN group together even when
 * they were captured on different records: `/crm-v2/contact/abc123XYZ9876543`
 * and `/crm-v2/contact/k9PqR2sT4uV6wX8yZ0aB` both become `/crm-v2/contact/:id`.
 *
 * Pure and heuristic — there is no route table to consult at runtime, so a
 * segment is treated as an id when it looks machine-generated rather than like
 * an English word a human put in a URL.
 */

/** Prefixed ids like `usr_ab12cd34`. */
const PREFIXED_ID = /^[a-z]{2,6}_[A-Za-z0-9]{6,}$/;

/** Firestore push-ids and similar: long, mixed-case/digit, no vowel rhythm. */
const LONG_OPAQUE = /^[A-Za-z0-9_-]{16,}$/;

/** Pure numbers of any length are record ids, never screen names. */
const NUMERIC = /^\d+$/;

/** UUIDs, with or without dashes. */
const UUID = /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/;

/**
 * Kebab-case English, allowing short version tokens: "potential-mandate-report"
 * and "crm-v2" are screen names; "k9PqR2sT4uV6wX8yZ0aB" is not.
 */
function isWordy(segment: string): boolean {
  const words = segment.split('-');
  return (
    words.length >= 2 &&
    words.every((w) => /^[a-z]{2,}$/.test(w) || /^v?\d{1,2}$/.test(w))
  );
}

function looksLikeId(segment: string): boolean {
  if (NUMERIC.test(segment)) return true;
  if (UUID.test(segment)) return true;
  if (PREFIXED_ID.test(segment)) return true;
  if (LONG_OPAQUE.test(segment)) return !isWordy(segment);
  // Shorter mixed-alphanumeric like "abc123" — digits inside a segment mean a
  // record reference more often than a screen name, unless it reads as
  // kebab-case words ("crm-v2").
  return (
    /\d/.test(segment) &&
    /[A-Za-z]/.test(segment) &&
    segment.length >= 6 &&
    !isWordy(segment)
  );
}

export function routePattern(pathname: string): string {
  const clean = pathname.split('?')[0].split('#')[0];
  const segments = clean.split('/').filter(Boolean);
  if (segments.length === 0) return '/';
  return (
    '/' +
    segments.map((seg) => (looksLikeId(seg) ? ':id' : seg)).join('/')
  );
}
